/**
 * T-Hangar Edge Classification
 *
 * T-shaped units have 8 edges instead of 4 (box units).
 * The wall between adjacent units is a Z-shaped partition:
 *   wing_segment → step_segment → tail_segment
 *
 * Edge naming:
 *   front   — door side (unit_width long)
 *   back    — opposite side (tail_width long)
 *   right_wing, right_step, right_tail — right partition (or exterior for last unit)
 *   left_wing,  left_step,  left_tail  — left partition (or exterior for first unit)
 */

import type { EdgeType, DoorSpec } from '../types'
import type { THangarParams } from './params'
import { calculateStepWidth } from './params'

// ============================================================================
// TYPES
// ============================================================================

/** All 8 edge positions of a T-shaped unit */
export type THangarEdgeSide =
  | 'front'
  | 'back'
  | 'right_wing'
  | 'right_step'
  | 'right_tail'
  | 'left_wing'
  | 'left_step'
  | 'left_tail'

/** Edge for a T-hangar unit */
export interface THangarUnitEdge {
  /** Edge identifier within the unit */
  id: THangarEdgeSide
  /** Edge classification for rendering */
  type: EdgeType
  /** Whether this edge can be shared with an adjacent unit */
  canBeShared: boolean
  /** Neighbor unit code that shares this edge */
  sharedWith?: string | null
  /** Door openings on this edge (front edge only, typically) */
  openings?: DoorSpec[]
  /** Length of this edge in feet */
  length_ft: number
  /** Height of this edge in feet */
  height_ft: number
}

// ============================================================================
// EDGE CREATION
// ============================================================================

/**
 * Create 8 edges for a T-hangar unit
 *
 * Partition convention: The unit with the LOWER index owns and renders
 * the shared wall. So:
 * - Left-side partition edges (shared with unit i-1) → don't render (neighbor owns it)
 * - Right-side partition edges (shared with unit i+1) → render (we own it)
 */
export function createTHangarEdges(
  unitIndex: number,
  unitCount: number,
  params: THangarParams,
  buildingCode: string,
  doorSpec?: DoorSpec
): THangarUnitEdge[] {
  const isFirst = unitIndex === 0
  const isLast = unitIndex === unitCount - 1
  const stepWidth = calculateStepWidth(params)

  const neighborCode = (idx: number) => `${buildingCode}-${String(idx + 1).padStart(2, '0')}`

  // Use provided door spec or fall back to param-based default
  const frontDoorSpec: DoorSpec = doorSpec ?? {
    offset_ft: (params.unit_width_ft - Math.min(params.default_door_width_ft, params.unit_width_ft - 4)) / 2,
    width_ft: Math.min(params.default_door_width_ft, params.unit_width_ft - 4),
    height_ft: params.default_door_height_ft,
    type: params.default_door_type,
  }

  const edges: THangarUnitEdge[] = [
    // Front — exterior with door (unit_width long)
    {
      id: 'front',
      type: 'exterior',
      canBeShared: false,
      openings: [frontDoorSpec],
      length_ft: params.unit_width_ft,
      height_ft: params.height_ft,
    },
    // Back — exterior, no door (tail_width long)
    {
      id: 'back',
      type: 'exterior',
      canBeShared: false,
      length_ft: params.tail_width_ft,
      height_ft: params.height_ft,
    },
    // Right-side edges (3 segments of the Z-wall)
    {
      id: 'right_wing',
      type: isLast ? 'exterior' : 'partition',
      canBeShared: !isLast,
      sharedWith: isLast ? null : neighborCode(unitIndex + 1),
      length_ft: params.wing_depth_ft,
      height_ft: params.height_ft,
    },
    {
      id: 'right_step',
      type: isLast ? 'exterior' : 'partition',
      canBeShared: !isLast,
      sharedWith: isLast ? null : neighborCode(unitIndex + 1),
      length_ft: stepWidth,
      height_ft: params.height_ft,
    },
    {
      id: 'right_tail',
      type: isLast ? 'exterior' : 'partition',
      canBeShared: !isLast,
      sharedWith: isLast ? null : neighborCode(unitIndex + 1),
      length_ft: params.wing_depth_ft,
      height_ft: params.height_ft,
    },
    // Left-side edges (3 segments of the Z-wall)
    {
      id: 'left_wing',
      type: isFirst ? 'exterior' : 'partition',
      canBeShared: !isFirst,
      sharedWith: isFirst ? null : neighborCode(unitIndex - 1),
      length_ft: params.wing_depth_ft,
      height_ft: params.height_ft,
    },
    {
      id: 'left_step',
      type: isFirst ? 'exterior' : 'partition',
      canBeShared: !isFirst,
      sharedWith: isFirst ? null : neighborCode(unitIndex - 1),
      length_ft: stepWidth,
      height_ft: params.height_ft,
    },
    {
      id: 'left_tail',
      type: isFirst ? 'exterior' : 'partition',
      canBeShared: !isFirst,
      sharedWith: isFirst ? null : neighborCode(unitIndex - 1),
      length_ft: params.wing_depth_ft,
      height_ft: params.height_ft,
    },
  ]

  return edges
}

/**
 * Determine if a T-hangar edge should be rendered by this unit.
 *
 * Convention: lower-index unit owns shared walls.
 * - Right-side partitions → render (we own it, shared with higher-index neighbor)
 * - Left-side partitions → don't render (lower-index neighbor owns it)
 * - Exterior → always render
 */
export function shouldRenderTHangarEdge(edge: THangarUnitEdge): boolean {
  if (edge.type === 'exterior') return true
  if (edge.type === 'internal') return false

  // Partition: right-side edges render, left-side edges skip
  if (edge.id.startsWith('right_')) return true
  if (edge.id.startsWith('left_')) return false

  return true
}
